import { ISelectOption } from 'components/forms/Selects'
import { IOption } from 'components/forms/combobox/types'
import { IFileBody } from 'types/file'
import { TRACKING_METHOD_TYPE } from 'types/tracking'
import { IStoreState, SORT_ORDER } from 'apps/fleet-planner/types/base'
import { IDriver } from 'apps/fleet-planner/types/driver'
import { IExternalEntities } from 'apps/fleet-planner/types/entity'
import { IFacilityAddress } from 'apps/fleet-planner/utils/api/facilities'
import {
  CUSTOMER_LABELS_TYPE,
  EQUIPMENT_TYPE,
  TAB,
  STOP_TYPE,
  LOAD_STATE,
  SUBSCRIBER_NOTIFICATION_TYPE,
  TEMPERATURE_TYPE,
} from '../constants/loads'
import { IActivity } from './activity'
import { IDispatcher } from './base'
import { ICarrier } from './carriers'
import { ICustomer, ICustomerContact } from './customers'

export const TRAILER_TYPES = {
  [EQUIPMENT_TYPE['Dry Van']]: [53, 48, 45, 43, 42, 40, 36, 32, 28, 20],
  [EQUIPMENT_TYPE.Reefer]: [53, 51, 48, 40, 36, 32, 28, 'Space Saver'],
  [EQUIPMENT_TYPE.Flatbed]: [53, 48, 45, 42, 40, 'Extendable'],
  [EQUIPMENT_TYPE['Step Deck']]: [53, 48],
  [EQUIPMENT_TYPE['Double Drop Deck']]: [53, 48],
  [EQUIPMENT_TYPE.Conestoga]: [53, 48],
  [EQUIPMENT_TYPE.Tanker]: [53, 50, 48, 45, 43, 32, 40],
  [EQUIPMENT_TYPE.Specialized]: [],
}

export interface ITruck {
  id?: string
  mcNumber?: number
  vin: string
  truckNumber: string
  motorCarrier?: ICarrier
  motorCarrierId?: number
  keepTruckinId?: number
  isP44Valid?: boolean
}

export interface ITrailer {
  id?: string
  trailerNumber: string
  trailerLength?: string
  mcNumber?: number
  trailerType?: EQUIPMENT_TYPE
  motorCarrier?: ICarrier
  motorCarrierId?: number
}

export interface ILoadSettings {
  notifySubscribersAbout: SUBSCRIBER_NOTIFICATION_TYPE
}

export type TPodState =
  | 'pod_pending'
  | 'pod_approved'
  | 'pod_rejected'
  | 'pod_in_review'
  | null
  | undefined

export interface ILoad {
  id?: string
  quickbooksInvoiceId?: string
  status: LOAD_STATE
  entityId?: string
  mcNumber?: number
  motorCarrier?: ICarrier
  trailerId?: number | null
  trailerNumber?: string // TODO: remove from ILoad definition, it is only use for create/update
  trailerLength?: string // TODO: remove from ILoad definition, it is only use for create/update
  trailerType?: EQUIPMENT_TYPE // TODO: remove from ILoad definition, it is only use for create/update
  truckId?: number | null
  truckNumber?: string // TODO: remove from ILoad definition, it is only use for create/update
  dispatcherId?: string
  notes?: string
  externalId?: string
  isSample?: boolean
  stops: IStop[]
  currentStopSequence?: string
  customer?: ICustomer
  customerId?: string
  customerNotes?: string // this is the load-specific Customer notes
  customerContact?: ICustomerContact
  customerContactId?: number
  drivers?: IDriver[]
  rate?: string
  miles?: string
  temperatureSetting?: ITemperatureSetting
  attachments?: IFile[]
  pods?: IFile[]
  podState?: TPodState
  truck?: ITruck | null
  trailer?: ITrailer | null
  dispatcher?: IDispatcher
  externalEntities?: IExternalEntities
  isExternal?: boolean
  instructionsFromCustomer?: string
  externalUpdatedAt?: string
  securingOptions?: string
  mostRecentActivity?: IActivity
  settings?: ILoadSettings
  subscribers?: ICustomerContact[]
  motorCarrierId?: number
  readOnly?: boolean
  /**
   * Used only in PUT request to update documents.
   * Each item requires a `fileType` value
   */
  allAttachments?: IFileAttachment[]
  /**
   * Currently selected tracking method for load
   */
  trackingMethod?: TRACKING_METHOD_TYPE
  customerLabels?: CUSTOMER_LABELS_TYPE[]
}

export interface ILocationELD {
  latitude: number
  longitude: number
  locatedAt: string
  locationSource: TRACKING_METHOD_TYPE
}

export interface IShipmentETA {
  eta: string
  distance: number
  onTime: boolean
}

export interface IShipmentLocation {
  /**
   * Array of last 4 location pings from ELD tracking source
   */
  recentLocations: ILocationELD[]
  eta: IShipmentETA
  /**
   * True if the most recent ping was for the current shipment
   */
  lastLocationIsForCurrentShipment?: boolean
}

export interface ITemperatureSetting {
  temperatureType?: TEMPERATURE_TYPE
  precool?: string
  minTemperature?: string
  maxTemperature?: string
  fixedTemperature?: string
}

export interface ILoadFilters {
  customerId?: string
  customerLabels?: CUSTOMER_LABELS_TYPE[]
  dispatcherId?: string
  driverId?: string
  dropoffDate?: string
  dropoffFacilityId?: string
  dropoffLocality?: string
  dropoffSubdivisionCode?: string
  maxAge?: number
  motorCarrierId?: number
  onlyNoTrailer?: boolean
  pickupDate?: string
  pickupFacilityId?: string
  pickupLocality?: string
  pickupSubdivisionCode?: string
  referenceNumber?: string
  startDate?: string
  status?:
    | LOAD_STATE[]
    | {
        internal: LOAD_STATE[]
        external: LOAD_STATE[]
      }
}

export interface ILoadsParams {
  pageNumber: number
  pageSize: number
  sortField?: SORT_FIELD
  sortOrder?: SORT_ORDER
  filters: ILoadFilters
  startAt?: string
  endAt?: string
}

export interface ILoadsData {
  loads: { byId: { [id: string]: ILoad } }
  loadIds: string[]
}

export interface ILoadsState extends IStoreState {
  data: ILoadsData
  totalCount: number
  activeTab: TAB
  params: ILoadsParams
  isFleet?: boolean
  lastCreatedLoad?: ILoad
  currentStatusFilters?: string
}

export interface ILoadCountsState extends IStoreState {
  counts: number[]
}

export interface ICarrierState extends IStoreState {
  carriers: ICarrier[]
  carriersSelect: ISelectOption<number>[]
}

export interface IDriverState extends IStoreState {
  drivers: IDriver[]
  driversAutocomplete: IOption[]
}

export interface IDispatcherState extends IStoreState {
  dispatchers: IDispatcher[]
  dispatchersAutocomplete: IOption[]
}

export interface IFacility {
  name: string
  address: string
  city: string
  stateCode: string
  countryCode: string
  zipcode: string
  dock: string
  instructions: string
  contact: string
  email: string
  phone: string
  ext: string
}

export interface IStop {
  externalId?: string
  id?: string
  stopType: STOP_TYPE
  stopSequence: number
  relativeStopSequence?: number
  startAt?: string
  endAt?: string
  checkedInAt?: string | null
  checkedOutAt?: string | null
  unit?: string
  unitQuantity?: string
  poNumber?: string
  notes?: string
  city?: string
  stateCode?: string
  countryCode?: string
  isDropTrailer?: boolean
  instructionsFromCustomer?: string
  bolNumber?: string
  referenceNumber?: string
  dropTrailerEmptyAt?: string | null
  dropTrailerArrivedAt?: string | null
  facilityContact?: ICustomerContact
  commodity?: string
  weight?: string
  uuid?: string
  displayTimezone?: string
  appointmentNumber?: string
  dockBuildingLot?: string
  instructions?: string

  // When creating a load, you can pass `name`
  // prop to save the facility
  facility?: {
    id?: string
    name?: string
  }

  // When updating or creating a Stop via PUT or POST /shipments
  // you can use `id` to reference a saved Facility address
  address?: Partial<IFacilityAddress>
}

export interface ILoadEquipment {
  trailerType?: EQUIPMENT_TYPE
  trailerLength?: string
  temperatureSetting?: ITemperatureSetting
}

export enum SORT_FIELD {
  MOST_RECENT_ACTIVITY = 'most_recent_activity',
  PICKUP = 'pickup',
  DROPOFF = 'dropoff',
}

export interface IFile extends IFileBody {
  _destroy?: boolean
  createdAt: string
  uuid: string
  url: string
  id: number
  size?: number
}

export type TFileType = 'pod' | 'scale_ticket' | 'misc' | 'rate_con' | 'payable' | 'private'

/**
 * Added for Transfix loads to account for document types
 */
export interface IFileAttachment extends IFileCreate {
  fileType: TFileType
  size?: number
}

export interface IFileAttachmentWithType<T extends TFileType> extends IFileAttachment {
  fileType: T
}

export interface IFileCreate extends IFileBody {
  _destroy?: boolean
  blobId?: number
  uuid?: string
  size?: number
}

export enum FLEET_RANGE {
  ONE_DAY = 1,
  TWO_DAYS = 2,
  THREE_DAYS = 3,
  WEEK = 7,
}

export interface ILoadStatusOption {
  status: LOAD_STATE
  currentStopSequence?: string
}
